import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../components/state/WorldStateContext.jsx';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  Mail,
  MailOpen,
  Star,
  AlertTriangle,
  Building2,
  Plane,
  Shield,
  User,
  Trash2,
  CheckCheck,
  X,
} from 'lucide-react';

// =============================================================================
// INBOX PAGE - Protocol 13: The Inbox
// Faux email client UI for game intel and communications
// =============================================================================

// Message type icons and colors
const MESSAGE_TYPE_CONFIG = {
  intel: { icon: User, color: '#a855f7', label: 'Intel' },
  system: { icon: AlertTriangle, color: '#f59e0b', label: 'System' },
  faa: { icon: Shield, color: '#ef4444', label: 'FAA' },
  airport: { icon: Building2, color: '#3b82f6', label: 'Airport' },
  competitor: { icon: Plane, color: '#22c55e', label: 'Competitor' },
};

// Format date for display (returns key info for translation)
function formatMessageDate(dateStr, gameTime, t, i18nLang) {
  const msgDate = new Date(dateStr);
  const now = new Date(gameTime);
  const diffMs = now - msgDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Determine locale for date formatting
  const locale = i18nLang === 'ja' ? 'ja-JP' : i18nLang === 'zh-CN' || i18nLang === 'zh' ? 'zh-CN' : 'en-US';

  if (diffDays === 0) {
    return msgDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  } else if (diffDays === 1) {
    return t('inbox.date.yesterday');
  } else if (diffDays < 7) {
    return t('inbox.date.daysAgo', { count: diffDays });
  } else {
    return msgDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
}

// Render stars for reliability
function ReliabilityStars({ stars, reliability, t }) {
  if (!stars && reliability === null) return null;

  return (
    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-700">
      <span className="text-gray-500 text-sm">{t('inbox.reliability')}:</span>
      {stars && (
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={`w-4 h-4 ${n <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
            />
          ))}
        </div>
      )}
      {reliability !== null && (
        <span className="text-gray-400 text-sm">
          ({reliability}%)
        </span>
      )}
    </div>
  );
}

export default function Inbox() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { worldState, markMessageRead, markAllMessagesRead, deleteInboxMessage, handleEmailAction } = useWorldState();

  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Helper to resolve email fields - checks for translation key first, falls back to direct value
  const getEmailField = (msg, field) => {
    const keyField = `${field}Key`;
    if (msg[keyField]) {
      // Has translation key - resolve it with interpolation if available
      return t(msg[keyField], msg.interpolation || {});
    }
    return msg[field] || '';
  };

  // Helper for action labels
  const getActionLabel = (action) => {
    if (action.labelKey) {
      return t(action.labelKey);
    }
    return action.label || '';
  };

  const messages = worldState?.inbox?.messages || [];
  const gameTime = worldState?.gameTime;

  // Filter messages by type
  const filteredMessages = useMemo(() => {
    if (filterType === 'all') return messages;
    if (filterType === 'unread') return messages.filter((m) => !m.read);
    return messages.filter((m) => m.type === filterType);
  }, [messages, filterType]);

  // Get selected message
  const selectedMessage = useMemo(() => {
    return messages.find((m) => m.id === selectedMessageId);
  }, [messages, selectedMessageId]);

  // Handle message selection
  const handleSelectMessage = (msg) => {
    setSelectedMessageId(msg.id);
    if (!msg.read) {
      markMessageRead(msg.id);
    }
  };

  // Unread count
  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0a0a0a', color: '#e5e5e5' }}
    >
      {/* Header Bar - Email Client Chrome */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/opscenter')}
            style={{ color: '#737373' }}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('inbox.opsCenter')}
          </Button>

          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6" style={{ color: '#60ffd0' }} />
            <h1 className="text-xl font-bold" style={{ color: '#60ffd0' }}>
              {t('inbox.title')}
            </h1>
            {unreadCount > 0 && (
              <Badge
                className="ml-2"
                style={{ backgroundColor: '#ef4444', color: '#fff' }}
              >
                {unreadCount}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllMessagesRead}
              style={{ color: '#737373' }}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              {t('inbox.markAllRead')}
            </Button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div
        className="flex items-center gap-1 px-4 py-2 border-b"
        style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a' }}
      >
        {[
          { key: 'all', label: t('inbox.filters.all') },
          { key: 'unread', label: `${t('inbox.filters.unread')} (${unreadCount})` },
          { key: 'intel', label: t('inbox.filters.intel') },
          { key: 'system', label: t('inbox.filters.system') },
          { key: 'faa', label: t('inbox.filters.faa') },
          { key: 'airport', label: t('inbox.filters.airports') },
          { key: 'competitor', label: t('inbox.filters.competitors') },
        ].map((tab) => (
          <Button
            key={tab.key}
            variant="ghost"
            size="sm"
            onClick={() => setFilterType(tab.key)}
            style={{
              backgroundColor: filterType === tab.key ? '#1a1a1a' : 'transparent',
              color: filterType === tab.key ? '#60ffd0' : '#737373',
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Main Content - Email List + Detail View */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Message List */}
        <div
          className="w-96 flex-shrink-0 border-r overflow-y-auto"
          style={{ backgroundColor: '#0a0a0a', borderColor: '#1a1a1a' }}
        >
          {filteredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <Mail className="w-12 h-12 mb-4 opacity-30" style={{ color: '#737373' }} />
              <p style={{ color: '#737373' }}>
                {filterType === 'all' ? t('inbox.noMessages') : t('inbox.noMessagesType', { type: t(`inbox.filters.${filterType}`) })}
              </p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const typeConfig = MESSAGE_TYPE_CONFIG[msg.type] || MESSAGE_TYPE_CONFIG.system;
              const TypeIcon = typeConfig.icon;
              const isSelected = selectedMessageId === msg.id;

              return (
                <div
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer border-b transition-colors"
                  style={{
                    backgroundColor: isSelected ? '#1a1a1a' : 'transparent',
                    borderColor: '#1a1a1a',
                  }}
                >
                  {/* Unread indicator */}
                  <div className="flex-shrink-0 w-2 pt-2">
                    {!msg.read && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: '#60ffd0' }}
                      />
                    )}
                  </div>

                  {/* Type icon */}
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${typeConfig.color}20` }}
                  >
                    <TypeIcon className="w-4 h-4" style={{ color: typeConfig.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`text-sm truncate ${!msg.read ? 'font-bold' : ''}`}
                        style={{ color: !msg.read ? '#e5e5e5' : '#a3a3a3' }}
                      >
                        {getEmailField(msg, 'from')}
                      </span>
                      <span className="text-xs flex-shrink-0" style={{ color: '#525252' }}>
                        {formatMessageDate(msg.date, gameTime, t, i18n.language)}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate mt-0.5 ${!msg.read ? 'font-medium' : ''}`}
                      style={{ color: !msg.read ? '#d4d4d4' : '#737373' }}
                    >
                      {getEmailField(msg, 'subject')}
                    </p>
                    <p className="text-xs truncate mt-1" style={{ color: '#525252' }}>
                      {getEmailField(msg, 'body').substring(0, 60)}...
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Panel - Message Detail */}
        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#0c0c0c' }}>
          {selectedMessage ? (
            <div className="p-6">
              {/* Email Header */}
              <div
                className="rounded-lg p-4 mb-4"
                style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-bold" style={{ color: '#e5e5e5' }}>
                        {getEmailField(selectedMessage, 'from')}
                      </span>
                      {(selectedMessage.fromTitle || selectedMessage.fromTitleKey) && (
                        <span className="text-sm" style={{ color: '#737373' }}>
                          ({getEmailField(selectedMessage, 'fromTitle')})
                        </span>
                      )}
                      {selectedMessage.stars && (
                        <div className="flex items-center gap-0.5 ml-2">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className={`w-3 h-3 ${
                                n <= selectedMessage.stars
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-sm" style={{ color: '#737373' }}>
                      <span>{t('inbox.detail.to')}: </span>
                      <span style={{ color: '#a3a3a3' }}>{t('inbox.detail.you')}</span>
                    </div>
                    <div className="text-sm mt-1" style={{ color: '#737373' }}>
                      <span>{t('inbox.detail.subject')}: </span>
                      <span style={{ color: '#a3a3a3' }}>{getEmailField(selectedMessage, 'subject')}</span>
                    </div>
                    <div className="text-sm mt-1" style={{ color: '#737373' }}>
                      <span>{t('inbox.detail.date')}: </span>
                      <span style={{ color: '#a3a3a3' }}>
                        {new Date(selectedMessage.date).toLocaleString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh-CN' || i18n.language === 'zh' ? 'zh-CN' : 'en-US')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge
                      style={{
                        backgroundColor: `${MESSAGE_TYPE_CONFIG[selectedMessage.type]?.color || '#737373'}20`,
                        color: MESSAGE_TYPE_CONFIG[selectedMessage.type]?.color || '#737373',
                        border: `1px solid ${MESSAGE_TYPE_CONFIG[selectedMessage.type]?.color || '#737373'}`,
                      }}
                    >
                      {MESSAGE_TYPE_CONFIG[selectedMessage.type]?.label || selectedMessage.type}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteInboxMessage(selectedMessage.id);
                        setSelectedMessageId(null);
                      }}
                      style={{ color: '#ef4444' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: '#111',
                  border: '1px solid #1a1a1a',
                  fontFamily: 'monospace',
                }}
              >
                <div
                  className="whitespace-pre-wrap leading-relaxed"
                  style={{ color: '#d4d4d4' }}
                >
                  {getEmailField(selectedMessage, 'body')}
                </div>

                {/* Action Buttons */}
                {selectedMessage.actions && selectedMessage.actions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-700 flex gap-2">
                    {selectedMessage.actions.map((action, idx) => (
                      <Button
                        key={idx}
                        onClick={() => {
                          if (handleEmailAction) {
                            handleEmailAction(action, selectedMessage);
                            // Navigate to WorldMap for hub auctions (modal is there)
                            if (action.type === 'open_hub_auction') {
                              navigate('/worldmap');
                            }
                          }
                        }}
                        style={{
                          backgroundColor: action.variant === 'primary' ? '#60ffd0' : '#1a1a1a',
                          color: action.variant === 'primary' ? '#000' : '#60ffd0',
                          border: action.variant === 'primary' ? 'none' : '1px solid #60ffd0',
                        }}
                      >
                        {getActionLabel(action)}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Reliability Footer */}
                <ReliabilityStars
                  stars={selectedMessage.stars}
                  reliability={selectedMessage.reliability}
                  t={t}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MailOpen className="w-16 h-16 mb-4 opacity-20" style={{ color: '#737373' }} />
              <p className="text-lg" style={{ color: '#737373' }}>
                {t('inbox.selectMessage')}
              </p>
              <p className="text-sm mt-2" style={{ color: '#525252' }}>
                {t('inbox.clickToRead')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer - Email Client Chrome */}
      <div
        className="flex items-center justify-between px-4 py-2 border-t text-xs"
        style={{ backgroundColor: '#0c0c0c', borderColor: '#1a1a1a', color: '#525252' }}
      >
        <span>{t('inbox.footer.messageCount', { count: messages.length })}</span>
        <span>{t('inbox.footer.systemName')}</span>
      </div>
    </div>
  );
}
